// SCHEMA_VERSION = 1
//
// Channel descriptor catalogue. Single source of truth for channel name,
// options, and presence capability. Consumed by AblyChannelRegistry on the
// frontend; safe to import on the API side where presence-eligibility metadata
// is also useful (gate checks, validation).
//
// We deliberately avoid importing from "ably" so this package does not pull
// the SDK as a dependency. RegistryChannelOptions contains Ably-native channel
// options plus registry-only lifecycle hints; the frontend registry strips
// those hints before calling the Ably SDK.

// ---------------------------------------------------------------------------
// Structural channel options
// ---------------------------------------------------------------------------

/** Mirror of the subset of Ably.ChannelMode literals we use. */
export type RegistryChannelMode =
  | "presence"
  | "publish"
  | "subscribe"
  | "presence_subscribe"
  | "PRESENCE"
  | "PUBLISH"
  | "SUBSCRIBE"
  | "PRESENCE_SUBSCRIBE";

/** Ably channel options plus registry-only lifecycle hints. */
export interface RegistryChannelOptions {
  modes?: RegistryChannelMode[];
  params?: Record<string, string>;
  cipher?: unknown;
  /** When false, attach is deferred until first subscribe. Default true. */
  attachOnSubscribe?: boolean;
}

// ---------------------------------------------------------------------------
// Channel descriptor catalogue
// ---------------------------------------------------------------------------

export type PresenceCapability = "none" | "reader-only" | "pusher-and-reader";

export interface ChannelDescriptor<
  TPresencePayload = never,
  TArgs extends unknown[] = unknown[],
  TPres extends PresenceCapability = PresenceCapability,
> {
  readonly name: (...args: TArgs) => string;
  readonly presence: TPres;
  readonly options: RegistryChannelOptions;
  /**
   * Phantom — typed presence payload shape. Only meaningful when
   * presence !== "none". NEVER read at runtime.
   */
  readonly _presencePayload?: TPresencePayload;
}

/**
 * Validate a built channel name against Ably's rules (INV-13).
 * Per https://ably.com/docs/channels: channel names are case sensitive,
 * cannot start with "[" or ":", cannot contain newlines, ≤2048 chars.
 *
 * Throws on hard violations; warns (dev only) on soft violations (length).
 */
export function validateChannelName(name: string): void {
  if (typeof name !== "string" || name.length === 0) {
    throw new Error(
      `Channel name ${JSON.stringify(name)} is invalid: must be a non-empty string.`,
    );
  }
  if (name.startsWith("[")) {
    throw new Error(
      `Channel name ${JSON.stringify(name)} is invalid: must not start with "[".`,
    );
  }
  if (name.startsWith(":")) {
    throw new Error(
      `Channel name ${JSON.stringify(name)} is invalid: must not start with ":".`,
    );
  }
  if (name.includes("\n")) {
    throw new Error(
      `Channel name ${JSON.stringify(name)} is invalid: must not contain newline characters.`,
    );
  }
  if (name.length > 2048) {
    // Soft-warn. Ably's documented soft limit; not a hard error.
    if (
      typeof globalThis !== "undefined" &&
      typeof (globalThis as { console?: Console }).console !== "undefined"
    ) {
      console.warn(
        `[realtime-channels] Channel name length (${name.length}) exceeds Ably's recommended soft-limit of 2048 characters.`,
      );
    }
  }
}

/**
 * Factory for creating a typed channel descriptor. Two overloads enforce
 * "presence: none" descriptors do not accept a payload type while presence-
 * bearing descriptors require one.
 */
export function defineChannel<TArgs extends unknown[]>(
  name: (...args: TArgs) => string,
  spec: { presence: "none"; options: RegistryChannelOptions },
): ChannelDescriptor<never, TArgs, "none">;
export function defineChannel<
  TPayload extends Record<string, unknown>,
  TArgs extends unknown[],
>(
  name: (...args: TArgs) => string,
  spec: {
    presence: "reader-only";
    options: RegistryChannelOptions;
    /**
     * NOTE: `payload` is a PHANTOM TYPE FACTORY — NEVER called at runtime.
     * It exists solely for TypeScript inference of the TPayload generic.
     */
    payload: () => TPayload;
  },
): ChannelDescriptor<TPayload, TArgs, "reader-only">;
export function defineChannel<
  TPayload extends Record<string, unknown>,
  TArgs extends unknown[],
>(
  name: (...args: TArgs) => string,
  spec: {
    presence: "pusher-and-reader";
    options: RegistryChannelOptions;
    /**
     * NOTE: `payload` is a PHANTOM TYPE FACTORY — NEVER called at runtime.
     * It exists solely for TypeScript inference of the TPayload generic.
     */
    payload: () => TPayload;
  },
): ChannelDescriptor<TPayload, TArgs, "pusher-and-reader">;
export function defineChannel(
  name: (...args: unknown[]) => string,
  spec: {
    presence: PresenceCapability;
    options: RegistryChannelOptions;
    payload?: () => unknown;
  },
): ChannelDescriptor<unknown, unknown[], PresenceCapability> {
  return {
    name,
    presence: spec.presence,
    options: spec.options,
  };
}

// ---------------------------------------------------------------------------
// Catalogue
// ---------------------------------------------------------------------------

/**
 * Phantom payload shape for fan-data presence channels.
 * Real shape lives in the frontend's FanData type; this is the structural
 * floor required by acquirePresencePusher's typed payload.
 */
type FanDataPresencePayloadShape = Record<string, unknown>;

const SUBSCRIBE_ONLY: RegistryChannelOptions = { modes: ["subscribe"] };

const PRESENCE_PUSH_AND_SUBSCRIBE: RegistryChannelOptions = {
  modes: ["presence", "presence_subscribe", "subscribe", "publish"],
};

/**
 * Catalogue of every realtime channel in the codebase. The registry consumes
 * these descriptors; consumers pass `CHANNELS.<name>` to subscribeChannel /
 * subscribePresence / acquirePresencePusher.
 *
 * Channel-name strings here MUST remain identical to the legacy
 * `REALTIME_CHANNELS` builders in realtime-state.ts and the
 * `getShowSocketsChannelName` / `getUserSocketsChannelName` patterns —
 * production wire-format compatibility relies on it.
 */
export const CHANNELS = {
  // -------- Non-presence channels (mirror legacy REALTIME_CHANNELS) --------
  showState: defineChannel((showId: string) => `fanfest:${showId}:show-state`, {
    presence: "none",
    options: SUBSCRIBE_ONLY,
  }),
  simulcastState: defineChannel(
    (showId: string) => `fanfest:${showId}:simulcast-state`,
    { presence: "none", options: SUBSCRIBE_ONLY },
  ),
  quiz: defineChannel((showId: string) => `fanfest:${showId}:quiz`, {
    presence: "none",
    options: { modes: ["publish", "subscribe"] },
  }),
  /** Zombie (TICKET-020) — quiz transitions folded into `quiz`. Kept per ROLLBACK-SAFETY-PLAN Rule 1; cleanup PR removes after ≥1 stable show. */
  quizTransitionEvents: defineChannel(
    (showId: string) => `fanfest:${showId}:quiz-transition-events`,
    { presence: "none", options: { modes: ["publish", "subscribe"] } },
  ),
  /**
   * Channel-scoped quiz-list channel. Quizzes are CHANNEL-scoped (a quiz row
   * has `channel_id`, not `show_id`), so this channel is keyed by `channelId`
   * — every viewer of any show on the channel, plus the channel page itself,
   * subscribes to the same channel. Carries the `quiz-list-changed` +
   * `quiz-winner` discriminated union. Keying by show id required a
   * channel->show lookup on the publish side that mis-routed deltas when a
   * channel had 2+ concurrent live shows; keying by channel id removes that
   * lookup. Mirrors `REALTIME_CHANNELS.quizList` in realtime-state.ts.
   */
  quizList: defineChannel(
    (channelId: string) => `fanfest:${channelId}:quiz-list`,
    {
      presence: "none",
      options: SUBSCRIBE_ONLY,
    },
  ),
  raffle: defineChannel((showId: string) => `fanfest:${showId}:raffle`, {
    presence: "none",
    options: SUBSCRIBE_ONLY,
  }),
  /**
   * Channel-scoped raffle-list channel. Raffles are CHANNEL-scoped (a raffle
   * row has `channel_id`, not `show_id`), so this channel is keyed by
   * `channelId` — every viewer of any show on the channel, plus the channel
   * page itself, subscribes to the same channel. Carries the
   * `raffle-list-changed` + `raffle-winner` discriminated union. Keying by
   * show id required a channel->show lookup on the publish side that
   * mis-routed deltas when a channel had 2+ concurrent live shows; keying by
   * channel id removes that lookup. Mirrors `REALTIME_CHANNELS.raffleList` in
   * realtime-state.ts.
   */
  raffleList: defineChannel(
    (channelId: string) => `fanfest:${channelId}:raffle-list`,
    { presence: "none", options: SUBSCRIBE_ONLY },
  ),
  layout: defineChannel((showId: string) => `fanfest:${showId}:layout`, {
    presence: "none",
    options: SUBSCRIBE_ONLY,
  }),
  agenda: defineChannel((showId: string) => `fanfest:${showId}:agenda`, {
    presence: "none",
    options: SUBSCRIBE_ONLY,
  }),
  /**
   * Dedicated presentation-plan channel. Carries the
   * `StageSlotStateDelta` discriminated delta only — plan deltas are NOT
   * folded onto `showStateV2` or the legacy `quiz` / `raffle` channels.
   * Subscribe-only: server-publish authority only, browser publish denied.
   * Mirrors `REALTIME_CHANNELS.stagePlan` in realtime-state.ts.
   */
  stagePlan: defineChannel((showId: string) => `fanfest:${showId}:stage-plan`, {
    presence: "none",
    options: SUBSCRIBE_ONLY,
  }),

  // -------- Presence-pushing channels --------
  // Audited list — the only legitimate pusher channels in plan-060 scope.
  fanDataPresence: defineChannel(
    (showId: string) => `fanfest:${showId}:fanfest-stage-presence`,
    {
      presence: "pusher-and-reader",
      options: PRESENCE_PUSH_AND_SUBSCRIBE,
      payload: () => ({}) as FanDataPresencePayloadShape,
    },
  ), // ZOMBIE per ROLLBACK-SAFETY-PLAN.md Rule 1 — cleanup PR removes this ≥7 days post-launch
  stageRoster: defineChannel(
    (showId: string) => `fanfest-stage-roster:${showId}:fan-data-state`,
    {
      presence: "pusher-and-reader",
      options: PRESENCE_PUSH_AND_SUBSCRIBE,
      payload: () => ({}) as FanDataPresencePayloadShape,
    },
  ),
  streamPreview: defineChannel(
    (userId: string) => `fanfest:${userId}:stream-preview`,
    {
      presence: "pusher-and-reader",
      options: PRESENCE_PUSH_AND_SUBSCRIBE,
      payload: () => ({}) as FanDataPresencePayloadShape,
    },
  ),

  // -------- Reader-only presence (occupancy) --------
  presenceCount: defineChannel(
    (channelSlug: string) => `fanfest:${channelSlug}:presence-count`,
    {
      presence: "reader-only",
      options: {
        modes: ["subscribe"],
        params: { occupancy: "metrics" },
        attachOnSubscribe: false,
      },
      payload: () => ({}) as Record<string, never>,
    },
  ),
  // -------- Additional non-presence channels --------
  channelState: defineChannel(
    (channelId: string) => `fanfest:${channelId}:channel-state`,
    { presence: "none", options: SUBSCRIBE_ONLY },
  ),
  simulfestEvents: defineChannel(
    (showId: string) => `fanfest:${showId}:simulfest`,
    { presence: "none", options: SUBSCRIBE_ONLY },
  ),
  participantsCount: defineChannel(
    (showId: string) => `fanfest:${showId}:participants-count`,
    { presence: "none", options: SUBSCRIBE_ONLY },
  ),
  peerUpdateEvents: defineChannel(
    (showId: string) => `fanfest:${showId}:peer-update-events`,
    { presence: "none", options: { modes: ["publish", "subscribe"] } },
  ),
  chatEvents: defineChannel(
    (_contextType: "show" | "channel", id: string) =>
      `fanfest-chat:${id}:chat-events`,
    { presence: "none", options: { modes: ["publish", "subscribe"] } },
  ),
  raffleSlotEvents: defineChannel(
    (showId: string) => `fanfest:${showId}:raffle-slot-events`,
    { presence: "none", options: { modes: ["publish", "subscribe"] } },
  ),
  raffleStepEvents: defineChannel(
    (showId: string) => `fanfest:${showId}:raffle-step-events`,
    { presence: "none", options: { modes: ["publish", "subscribe"] } },
  ),
  // -------- User-scoped event channels --------
  /**
   * Per-user event channel. The API publishes loyalty point updates here;
   * the frontend subscribes to display earning notifications.
   * Floor token grants api-events:* subscribe+history — subscribe-only mode
   * matches the token capability and avoids the attach rejection that occurs
   * when requesting publish mode without a matching grant.
   */
  userEvents: defineChannel((userId: string) => `api-events:user:${userId}`, {
    presence: "none",
    options: SUBSCRIBE_ONLY,
  }),
} as const;

export type ChannelsCatalogue = typeof CHANNELS;
